import React, { FC, useEffect, useState, useRef } from 'react';
import { Box, Button, Flex, Text } from 'src/components/ui';
import { Tabs, TabList, TabPanels, Tab, TabPanel } from '@chakra-ui/react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import GoogleMapsAutocomplete from '../common/form/GoogleMapsAutocomplete';
import GoogleMapsComponent from '../common/form/GoogleMapsComponent';
import { StringInputHook } from '../common/form';
import CustomModal from 'src/components/common/CustomModal';
import AlertMessage from '../common/AlertMessage';

const ServiceAreaFormSchema = z.object({
	name: z.string().min(1, 'Name is required'),
	radius: z.string().optional(),
	entityType: z.enum(['neighborhood', 'city', 'county', 'state', 'country']).optional(),
});

type ServiceAreaFormData = z.infer<typeof ServiceAreaFormSchema>;

type AddressWithFormatted = {
	street: string | null;
	city: string | null;
	state: string | null;
	postalCode: string | null;
	country: string;
	lat: string | number;
	long: string | number;
	formatted?: string;
	placeId?: string;
	regionCode?: string;
};

interface ServiceAreaDraft {
	type?: 'radius' | 'geographic';
	name: string;
	address?: AddressWithFormatted;
	radius?: string;
	geographic?: {
		entityType: 'neighborhood' | 'city' | 'county' | 'state' | 'country';
		entityId?: string;
		entityName: string;
		country: string;
		state?: string;
		displayName?: string;
	};
}

interface ServiceAreaModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (serviceAreaData: ServiceAreaDraft) => Promise<void>;
	serviceAreaDraft: ServiceAreaDraft;
	editingServiceAreaIdx?: number | null;
	isSaving?: boolean;
}

const ServiceAreaModal: FC<ServiceAreaModalProps> = ({
	isOpen,
	onClose,
	onSave,
	serviceAreaDraft,
	editingServiceAreaIdx = null,
	isSaving = false,
}) => {
	const formMethods = useForm<ServiceAreaFormData>({
		resolver: zodResolver(ServiceAreaFormSchema),
		defaultValues: {
			name: '',
			radius: '',
		},
	});

	const { handleSubmit, reset } = formMethods;

	// Service area type state
	const [serviceAreaType, setServiceAreaType] = useState<'radius' | 'geographic'>('radius');

	// Local state for address (since it's not part of the main form schema)
	const [address, setAddress] = useState<AddressWithFormatted>({
		street: null,
		city: null,
		state: null,
		postalCode: null,
		country: '',
		lat: '',
		long: '',
		formatted: '',
		placeId: '',
		regionCode: '',
	});

	// State for geographic entity
	const [geographic, setGeographic] = useState<{
		entityType: 'neighborhood' | 'city' | 'county' | 'state' | 'country';
		entityId?: string;
		entityName: string;
		country: string;
		state?: string;
		displayName?: string;
		lat?: number;
		lng?: number;
	} | null>(null);

	const [addressError, setAddressError] = useState<string>('');
	const [apiError, setApiError] = useState<string>('');

	// Map interaction state to prevent programmatic updates after user interaction
	const [mapKey, setMapKey] = useState<string>('initial');

	// Reset form when modal opens/closes or when serviceAreaDraft changes
	// Note: reset function from react-hook-form is stable and doesn't need to be in deps
	useEffect(() => {
		if (isOpen && serviceAreaDraft) {
			// Determine type from draft (default to 'radius' for backward compatibility)
			const type = serviceAreaDraft.type || 'radius';
			setServiceAreaType(type);

			reset({
				name: serviceAreaDraft.name,
				radius: serviceAreaDraft.radius || '',
				entityType: serviceAreaDraft.geographic?.entityType,
			});

			if (type === 'radius') {
				setAddress(serviceAreaDraft.address || {
					street: null,
					city: null,
					state: null,
					postalCode: null,
					country: '',
					lat: '',
					long: '',
					formatted: '',
					placeId: '',
					regionCode: '',
				});
				setGeographic(null);
			} else {
				setGeographic(serviceAreaDraft.geographic || null);
			}

			setAddressError('');
			setApiError('');

			// Reset map interaction state and force map refresh
			setMapKey(`edit-${Date.now()}`);
		} else if (isOpen) {
			// Reset for new service area
			setServiceAreaType('radius');
			setGeographic(null);
			setMapKey(`new-${Date.now()}`);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, serviceAreaDraft]);

	// Geocode geographic location when coordinates are missing
	useEffect(() => {
		if (!isOpen || serviceAreaType !== 'geographic' || !geographic) return;

		// Skip if already has coordinates
		if (geographic.lat && geographic.lng) return;

		// Check if Google Maps API is loaded
		if (!window.google || !window.google.maps) {
			// API not loaded yet, try again after a delay
			const retryTimer = setTimeout(() => {
				setMapKey(`retry-${Date.now()}`);
			}, 500);
			return () => clearTimeout(retryTimer);
		}

		// Build search query from entity name and country
		const searchQuery = geographic.displayName || geographic.entityName;
		if (!searchQuery) return;

		// Build full search query with country for better accuracy
		const fullQuery = geographic.country
			? `${searchQuery}, ${geographic.country}`
			: searchQuery;

		// Use Google Maps Geocoder to get coordinates
		const geocoder = new window.google.maps.Geocoder();
		geocoder.geocode({ address: fullQuery }, (results, status) => {
			if (status === 'OK' && results && results[0]) {
				const location = results[0].geometry.location;
				setGeographic(prev => prev ? {
					...prev,
					lat: location.lat(),
					lng: location.lng(),
				} : null);
				// Force map refresh with new coordinates
				setMapKey(`geocoded-${Date.now()}`);
			}
		});
	}, [isOpen, serviceAreaType, geographic, mapKey]);

	const onSubmit = handleSubmit(async (formData) => {
		setAddressError('');
		setApiError('');

		if (serviceAreaType === 'radius') {
			// Validate radius-based service area
			if (!address.country?.trim()) {
				setAddressError('Country is required');
				return;
			}
			if (!address.lat?.toString().trim()) {
				setAddressError('Location coordinates are required');
				return;
			}
			if (!address.long?.toString().trim()) {
				setAddressError('Location coordinates are required');
				return;
			}
			if (!formData.radius || formData.radius.trim() === '') {
				setAddressError('Radius is required');
				return;
			}

			const normalizedAddress: AddressWithFormatted = {
				...address,
				street: address.street?.trim() || null,
				city: address.city?.trim() || null,
				state: address.state?.trim() || null,
				postalCode: address.postalCode?.trim() || null,
				country: address.country?.trim() || '',
			};

			const serviceAreaData: ServiceAreaDraft = {
				type: 'radius',
				...formData,
				address: normalizedAddress,
			};

			try {
				await onSave(serviceAreaData);
			} catch (error: any) {
				setApiError(error.message || 'Failed to save service area');
			}
		} else {
			// Validate geographic entity service area
			if (!geographic) {
				setAddressError('Please select a geographic entity');
				return;
			}
			if (!geographic.entityName || !geographic.country) {
				setAddressError('Invalid geographic entity');
				return;
			}

			const serviceAreaData: ServiceAreaDraft = {
				type: 'geographic',
				name: formData.name,
				geographic: geographic,
			};

			try {
				await onSave(serviceAreaData);
			} catch (error: any) {
				setApiError(error.message || 'Failed to save service area');
			}
		}
	});

	const handleAddressChange = (
		newAddress: AddressWithFormatted,
		formatted: string,
	) => {
		setAddress({ ...newAddress, formatted });
		if (addressError) setAddressError('');

		// Update map center and zoom only when selecting new address from autocomplete
		if (
			newAddress.lat &&
			newAddress.long &&
			!isNaN(Number(newAddress.lat)) &&
			!isNaN(Number(newAddress.long))
		) {
			// Force map refresh with new address
			setMapKey(`address-${Date.now()}`);
		}
	};

	const handleAddressInputChange = (value: string) => {
		setAddress((prev) => ({ ...prev, formatted: value }));
		if (addressError) setAddressError('');
	};

	const handleMapMarkerDrag = (lat: number, lng: number) => {
		setAddress((prev) => ({
			...prev,
			lat: lat.toString(),
			long: lng.toString(),
		}));
	};

	const handlePlaceSelect = (placeId: string, addressDetails: any) => {
		if (serviceAreaType === 'radius') {
			setAddress((prev) => ({
				...prev,
				lat: addressDetails.lat,
				long: addressDetails.long,
				street: addressDetails.street || null,
				city: addressDetails.city || null,
				state: addressDetails.state || null,
				postalCode: addressDetails.postalCode || null,
				country: addressDetails.country || '',
				placeId: placeId,
				regionCode: addressDetails.regionCode,
			}));
		} else {
			// For geographic entities, extract the selected location name from formatted address
			// Formatted address is like "Manhattan, New York, NY, USA" - first part is what user selected
			const formattedAddress = addressDetails.formatted || '';
			const selectedLocationName = formattedAddress.split(',')[0]?.trim() || '';

			// Auto-detect entity type based on what was selected
			let entityType: 'neighborhood' | 'city' | 'county' | 'state' | 'country' = 'city';
			let entityName = selectedLocationName || addressDetails.city || '';
			let displayName = selectedLocationName || addressDetails.city || '';

			// Determine entity type based on the selected location
			// If the selected name differs from the city name, it's likely a neighborhood/sublocality
			if (selectedLocationName && addressDetails.city && selectedLocationName !== addressDetails.city) {
				entityType = 'neighborhood';
			} else if (addressDetails.types?.includes('sublocality') || addressDetails.types?.includes('neighborhood')) {
				entityType = 'neighborhood';
			} else if (addressDetails.city && !addressDetails.types?.includes('administrative_area_level_1')) {
				entityType = 'city';
			} else if (addressDetails.types?.includes('administrative_area_level_2')) {
				entityType = 'county';
				entityName = addressDetails.county || selectedLocationName || '';
				displayName = addressDetails.county || selectedLocationName || '';
			} else if (addressDetails.state && !addressDetails.city) {
				entityType = 'state';
				entityName = addressDetails.state;
				displayName = addressDetails.state;
			} else if (addressDetails.country && !addressDetails.state && !addressDetails.city) {
				entityType = 'country';
				entityName = addressDetails.country;
				displayName = addressDetails.country;
			}

			setGeographic({
				entityType,
				entityId: placeId,
				entityName,
				country: addressDetails.country || '',
				state: addressDetails.state || undefined,
				displayName,
				lat: addressDetails.lat ? Number(addressDetails.lat) : undefined,
				lng: addressDetails.long ? Number(addressDetails.long) : undefined,
			});
			if (addressError) setAddressError('');

			// Force map refresh when geographic entity is selected
			setMapKey(`geographic-${Date.now()}`);
		}
	};

	const handleTypeChange = (index: number) => {
		const newType = index === 0 ? 'radius' : 'geographic';
		setServiceAreaType(newType);
		setAddressError('');
		setApiError('');
	};

	// Get appropriate zoom level for entity type
	const getZoomForEntityType = (entityType?: string): number => {
		switch (entityType) {
			case 'neighborhood': return 13;
			case 'city': return 11;
			case 'county': return 9;
			case 'state': return 6;
			case 'country': return 4;
			default: return 11;
		}
	};

	const isEditing = editingServiceAreaIdx !== null;

	if (!isOpen) return null;

	return (
		<CustomModal
			isOpen={isOpen}
			onClose={onClose}
			header={isEditing ? 'Edit Service Area' : 'Add Service Area'}
			footer={
				<Flex
					w="full"
					justifyContent={{ base: 'stretch', md: 'flex-end' }}
					gap={{ base: 3, md: 2 }}
					direction={{ base: 'column-reverse', md: 'row' }}
				>
					<Button onClick={onClose} variant="ghost" w={{ base: 'full', md: 'auto' }}>
						Cancel
					</Button>
					<Button
						onClick={onSubmit}
						variant="orangeSolid"
						isLoading={isSaving}
						loadingText="Saving..."
						w={{ base: 'full', md: 'auto' }}
					>
						Save
					</Button>
				</Flex>
			}
			size="xl"
			stickyFooter
			maxWidth={{ base: '95vw', md: 'xl' }}
			minWidth={{ base: '95vw', md: 'xl' }}
		>
			<FormProvider {...formMethods}>
				<form onSubmit={onSubmit}>
					<Flex direction="column" gap={3} p={2} minH="400px">
						{apiError && <AlertMessage status="error">{apiError}</AlertMessage>}

						<StringInputHook
							name="name"
							label="Area Name"
							placeholder="e.g. Downtown Zone"
							required
						/>

						{/* Service Area Type Selector using Tabs */}
						<Tabs
							index={serviceAreaType === 'radius' ? 0 : 1}
							onChange={handleTypeChange}
							variant="enclosed"
						>
							<TabList>
								<Tab>Defined by Radius</Tab>
								<Tab>Location</Tab>
							</TabList>

							<TabPanels>
								{/* Radius-based fields */}
								<TabPanel px={0} pt={4}>
									<Flex direction="column" gap={3}>
										<GoogleMapsAutocomplete
											value={address.formatted || ''}
											onChange={handleAddressChange}
											onInputChange={handleAddressInputChange}
											error={addressError}
											label="Center Point"
											placeholder="Enter city, address, or place name"
											required
											autoSearch={!!(address.formatted && !address.lat)}
										/>
										<StringInputHook
											name="radius"
											label="Area Radius (miles)"
											placeholder="Radius in miles"
											required
										/>
										<Box mt={3} mb={2} minH="300px" w="100%">
											<GoogleMapsComponent
												mapKey={mapKey}
												markerPosition={
													address.lat &&
													address.long &&
													!isNaN(Number(address.lat)) &&
													!isNaN(Number(address.long)) &&
													address.lat.toString().trim() !== '' &&
													address.long.toString().trim() !== ''
														? { lat: Number(address.lat), lng: Number(address.long) }
														: undefined
												}
												onMarkerDrag={handleMapMarkerDrag}
												onPlaceSelect={handlePlaceSelect}
												circleRadius={
													formMethods.watch('radius') &&
													!isNaN(Number(formMethods.watch('radius')))
														? Number(formMethods.watch('radius'))
														: undefined
												}
											/>
										</Box>
									</Flex>
								</TabPanel>

								{/* Geographic entity fields */}
								<TabPanel px={0} pt={4}>
									<Flex direction="column" gap={3}>
										<GoogleMapsAutocomplete
											value={geographic?.displayName || ''}
											onChange={(newAddress, formatted) => {
								// Call handlePlaceSelect with the address data
								if (newAddress.placeId) {
									handlePlaceSelect(newAddress.placeId, {
										lat: newAddress.lat,
										long: newAddress.long,
										city: newAddress.city,
										state: newAddress.state,
										country: newAddress.country,
										street: newAddress.street,
										postalCode: newAddress.postalCode,
										regionCode: newAddress.regionCode,
										types: [],
										formatted: formatted, // Pass formatted address to extract location name
									});
								}
							}}
											onInputChange={(value) => {
												if (addressError) setAddressError('');
											}}
											error={addressError}
											label="Search Location"
											placeholder="Search for city, county, state, or country..."
											required
										/>

										{/* Map for geographic entity */}
										<Box mt={3} mb={2} minH="300px" w="100%">
											<GoogleMapsComponent
												mapKey={mapKey}
												markerPosition={
													geographic?.lat &&
													geographic?.lng &&
													!isNaN(Number(geographic.lat)) &&
													!isNaN(Number(geographic.lng))
														? { lat: Number(geographic.lat), lng: Number(geographic.lng) }
														: undefined
												}
												onPlaceSelect={handlePlaceSelect}
												zoom={getZoomForEntityType(geographic?.entityType)}
											/>
										</Box>
									</Flex>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</Flex>
				</form>
			</FormProvider>
		</CustomModal>
	);
};

export default ServiceAreaModal;
